// Hook TanStack Query — les environnements (GET /api/environnements), rafraichis toutes les 5 minutes.
//
// FORME : recopiee du contrat serveur `apps/admin/server/services/environnements/contrat.ts`,
// qui la VALIDE par Zod a la sortie. Recopiee et non importee : le projet client a
// `rootDir: ./src` (meme raison que useParcSante.ts). Un champ ajoute au contrat s ajoute ici.
//
// Cle de cache SANS companyId, et c est voulu (ADR-032) : c est l etat de la PLATEFORME,
// reserve a l administrateur d organisation — aucune donnee de compagnie.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@gs/auth';
import { api } from '../services/api';
import type { Motif } from './useParcSante';

export type Sortie = 'courriel' | 'textos' | 'fichiers' | 'synchronisation';
export type EtatDuCote = 'allume' | 'eteint' | 'absent' | 'inconnu';
export type Raison =
  | 'azure'
  | 'repond'
  | 'sans-droit-ni-reponse'
  | 'sans-adresse-ni-droit'
  | 'emplacement-inattendu'
  | 'autre-application';
export type EtatDeSortie = 'reelle' | 'coupee' | 'redirigee' | 'inactive' | 'inconnu';

export type Sonde =
  | { etat: 'sans-adresse' }
  | {
      etat: 'faite';
      repond: boolean;
      httpStatus: number | null;
      erreur: 'delai' | 'adresse-inconnue' | 'injoignable' | 'http' | null;
      application: string | null;
      version: string | null;
      commit: string | null;
      construitLe: string | null;
      emplacement: 'production' | 'preprod' | null;
      sorties: Record<string, string> | null;
    };

export type LectureAzure =
  | { etat: 'illisible'; motif: Motif }
  | { etat: 'lu'; existe: boolean; etatAzure: string | null };

export interface Cote {
  adresse: string | null;
  etat: EtatDuCote;
  raison: Raison;
  sonde: Sonde;
  azure: LectureAzure;
  sorties: { sortie: Sortie; etat: EtatDeSortie }[];
}

export interface AppEnvironnements {
  cle: string;
  site: string;
  nom: { fr: string; en: string };
  niveau: 'poc' | 'interne' | 'terrain';
  chemin: 'par-la-copie' | 'direct';
  production: Cote;
  copie: Cote;
}

export interface Environnements {
  mesureA: string;
  apps: AppEnvironnements[];
}

export const ENVIRONNEMENTS_QUERY_KEY = ['environnements'] as const;
const CINQ_MINUTES_MS = 5 * 60_000;

export function useEnvironnements() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ENVIRONNEMENTS_QUERY_KEY,
    queryFn: () => api.get<Environnements>('/api/environnements'),
    enabled: !!user,
    refetchInterval: CINQ_MINUTES_MS,
    staleTime: CINQ_MINUTES_MS,
  });
}

/** « Remesurer » : le serveur ne relance une vraie mesure que si la sienne a plus d une minute. */
export function useRemesurerEnvironnements() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<Environnements>('/api/environnements?remesurer=1'),
    onSuccess: (donnees) => client.setQueryData(ENVIRONNEMENTS_QUERY_KEY, donnees),
  });
}
